import React, {useState, useEffect} from 'react';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes} from '../../../api/consts';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import SMCModal from '../../../components/core/SMCModal';
import {useTranslation} from 'react-i18next';
import {ServiceDomainCreateSchema} from '../../../api/DBSchema/ServiceDomain';
import ServiceDomainWizardBody from './ServiceDomainWizardBody';
import {useIsMountedRef} from '../../../utils/hooks';
import {SystemSettingsGetSchema} from "../../../api/DBSchema/SystemSettings";

function ServiceDomainWizard({
    isOpen,
    onRequestClose,
    onSuccess
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const isMountedRef = useIsMountedRef();

    const [isLoading, setIsLoading] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [company, setCompany] = useState('');
    const [contact, setContact] = useState('');
    const [email, setEmail] = useState('');
    const [serverGroups, setServerGroups] = useState({
        defaultServerGroup: '',
        availableServerGroups: []
    })
    const [serverGroup, setServerGroup] = useState('');
    const [isTwoStepAuthenticationEnabled, setIsTwoStepAuthenticationEnabled] = useState(false);

    const onFinish = async () => {
        const createServiceDomainRequestObj = {
            [ServiceDomainCreateSchema.name]: name,
            [ServiceDomainCreateSchema.description]: description,
            [ServiceDomainCreateSchema.company]: company,
            [ServiceDomainCreateSchema.contact]: contact,
            [ServiceDomainCreateSchema.email]: email,
            [ServiceDomainCreateSchema.serverGroup]: serverGroup,
            [ServiceDomainCreateSchema.enableTwoStepAuthentication]: isTwoStepAuthenticationEnabled
        }
        setIsLoading(true);
        Api.createObject(ObjectTypes.SERVICE_DOMAIN, createServiceDomainRequestObj)
            .then(() => {
                if (isMountedRef.current) {
                    onSuccess(name);
                }
            })
            .catch (e => {
                if (isMountedRef.current) {
                    const errorMessage = getErrorMessage(e);
                    alert(errorMessage);
                }
            })
            .finally(() => {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
            });
    }

    // Reset to defaults on every open
    useEffect(() => {
        if (isOpen) {
            setName('');
            setDescription('');
            setCompany('');
            setContact('');
            setEmail('');
            setServerGroup(serverGroups.defaultServerGroup);
            setIsTwoStepAuthenticationEnabled(false);
        }
    }, [isOpen, serverGroups]);


    useEffect(() => {
        const fetchData = async () => {
            const fetchSystemSettingsResp = await Api.fetchSingleObject(ObjectTypes.SYSTEM_SETTINGS);
            const systemSettings = fetchSystemSettingsResp.data.data;
            const pageSchema = SystemSettingsGetSchema.pages.general;
            const pageId = pageSchema.pageId;
            if (systemSettings.hasOwnProperty(pageId)) {
                const pageData = systemSettings[pageId];
                const defaultServerGroupName = pageData[pageSchema.defaultServerGroupName];
                const availableServerGroups = pageData[pageSchema.availableServerGroups];
                setServerGroups({
                    defaultServerGroup: defaultServerGroupName || "",
                    availableServerGroups: availableServerGroups || []
                })
                setServerGroup(defaultServerGroupName);
            }
        }
        fetchData();
    }, [])


    const wizardVars = {
        name, setName,
        description, setDescription,
        company, setCompany,
        contact, setContact,
        email, setEmail,
        isTwoStepAuthenticationEnabled, setIsTwoStepAuthenticationEnabled,
        serverGroup, setServerGroup
    }
    
    return (
        <SMCModal
            title={t('SMP.CREATE_NEW_SERVICE_DOMAIN.CREATE_NEW_SERVICE_DOMAIN')}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            width={630}
            height={600}
            loading={isLoading}
        >
            <ServiceDomainWizardBody
                onFinish={onFinish}
                onCancel={onRequestClose}
                wizardVars={wizardVars}
                defaultServerGroup={serverGroups.defaultServerGroup}
                availableServerGroups={serverGroups.availableServerGroups}
            />
        </SMCModal>
    );
}

export default ServiceDomainWizard;